/**
 * Shared types for the step rule-set editor surface.
 *
 * These mirror the `StepRuleSetFields` GraphQL fragment. They are duplicated
 * here as plain TypeScript types so the editors stay decoupled from any
 * specific generated Apollo selection-set type and so a single source of truth
 * exists for the enum values rendered in the UI.
 */

export type ScoringAggregation = "SUM" | "AVERAGE" | "POINTS" | "FORMULA";

export type TiebreakerType =
  | "HEAD_TO_HEAD"
  | "SCORE_DIFFERENCE"
  | "TOTAL_SCORE"
  | "WINS"
  | "BUCHHOLZ"
  | "SONNBORN_BERGER"
  | "MEDIAN_BUCHHOLZ"
  | "CUMULATIVE"
  | "CUSTOM_FORMULA";

export type SortOrder = "ASC" | "DESC";

export type TriggerType =
  | "MATCH_ENDED"
  | "GAME_ENDED"
  | "ROUND_ENDED"
  | "GROUP_ENDED"
  | "STEP_ENDED"
  | "MANUAL";

export type ActionType = "ADVANCE" | "ELIMINATE" | "END" | "EXECUTE_LUA";

export type RulePurpose = "MATCH_SERIES_RESOLUTION" | null;

export type SourceRelative = "PREVIOUS" | "NEXT" | "CURRENT";

export type DataSource = "RANKINGS" | "SCORES" | "TEAMS";

export type UsageType = "SEED_ORDER" | "QUALIFY" | "INJECT_SCORE";

export type PresetName =
  | "SINGLE_ELIM"
  | "DOUBLE_ELIM"
  | "DOUBLE_ELIM_TRUE_FINALS"
  | "TRIPLE_ELIM"
  | "ROUND_ROBIN"
  | "ROUND_ROBIN_BO3"
  | "LEAGUE_HOME_AWAY"
  | "SWISS"
  | "SWISS_ACCELERATED"
  | "SWISS_MCMAHON"
  | "BUCHHOLZ_GROUPS"
  | "SINGLE_ELIM_CONSOLATION"
  | "RANDOM_DRAW_KNOCKOUT"
  | "STEP_LADDER"
  | "PAGE_PLAYOFF"
  | "GAUNTLET"
  | "GSL_FORMAT"
  | "ROUND_ROBIN_PLAYOFFS"
  | "BATTLE_ROYALE"
  | "POINTS_RACE"
  | "CONQUEST"
  | "KING_OF_THE_HILL_BO5";

export interface TiebreakerView {
  order: number;
  type: TiebreakerType;
  formula: string | null;
  sortOrder: SortOrder;
}

export interface ScoringRuleSetView {
  aggregation: ScoringAggregation;
  formula: string | null;
  winPoints: number;
  lossPoints: number;
  drawPoints: number;
  forfeitPoints: number;
  tiebreakers: TiebreakerView[];
}

export interface AdvancementActionView {
  type: ActionType;
  script: string | null;
}

export interface AdvancementRuleView {
  id: string;
  name: string;
  description: string | null;
  order: number;
  triggerType: TriggerType;
  triggerScope: string | null;
  condition: string;
  actions: AdvancementActionView[];
  autoExecute: boolean;
  allowManualOverride: boolean;
  enabled: boolean;
  purpose: RulePurpose;
}

export interface CrossStepUsageView {
  type: UsageType;
  condition: string | null;
  formula: string | null;
}

export interface CrossStepRuleView {
  sourceStepId: string | null;
  sourceRelative: SourceRelative | null;
  dataSource: DataSource;
  usage: CrossStepUsageView;
}

export interface GameTemplateView {
  id: string;
  order: number;
  teamSlots: number;
  bestOf: number;
  onWinTarget: string | null;
  onWinSourceSlot: number | null;
  onWinTargetSlot: number | null;
  onLossTarget: string | null;
  onLossSourceSlot: number | null;
  onLossTargetSlot: number | null;
}

export interface RoundTemplateView {
  id: string;
  name: string;
  order: number;
  gameTemplates: GameTemplateView[];
}

export interface GroupTemplateView {
  id: string;
  name: string;
  description: string | null;
  order: number;
  teamSlotsExpr: string;
  generationScript: string | null;
  bestOfExpr: string;
  teamSlotsPerGameExpr: string;
  roundCountExpr: string | null;
  roundTemplates: RoundTemplateView[];
}

export interface StructureTemplateView {
  groupTemplates: GroupTemplateView[] | null;
}

export interface StepRuleSetView {
  id: string;
  version: number;
  presetName: PresetName | null;
  teamCount: number | null;
  validated: boolean;
  validatedAt: string | null;
  scoringRuleSet: ScoringRuleSetView | null;
  advancementRules: AdvancementRuleView[];
  crossStepRules: CrossStepRuleView[];
  structureTemplate: { groupTemplates: GroupTemplateView[] | null } | null;
}

export interface StepRuleSetQueryResult {
  stepRuleSet?: StepRuleSetView | null;
}

export const TRIGGER_LABELS: Record<TriggerType, string> = {
  MATCH_ENDED: "Match ended",
  GAME_ENDED: "Game ended",
  ROUND_ENDED: "Round ended",
  GROUP_ENDED: "Group ended",
  STEP_ENDED: "Step ended",
  MANUAL: "Manual",
};

export const ACTION_LABELS: Record<ActionType, string> = {
  ADVANCE: "Advance",
  ELIMINATE: "Eliminate",
  END: "End",
  EXECUTE_LUA: "Execute Lua",
};

export const ACTION_COLORS: Record<ActionType, string> = {
  ADVANCE: "green",
  ELIMINATE: "red",
  END: "gray",
  EXECUTE_LUA: "indigo",
};

export const SOURCE_RELATIVE_LABELS: Record<SourceRelative, string> = {
  PREVIOUS: "Previous step",
  NEXT: "Next step",
  CURRENT: "Current step",
};

export const DATA_SOURCE_LABELS: Record<DataSource, string> = {
  RANKINGS: "Rankings",
  SCORES: "Scores",
  TEAMS: "Teams",
};

export const USAGE_TYPE_LABELS: Record<UsageType, string> = {
  SEED_ORDER: "Seed order",
  QUALIFY: "Qualify",
  INJECT_SCORE: "Inject score",
};

export const USAGE_TYPE_COLORS: Record<UsageType, string> = {
  SEED_ORDER: "blue",
  QUALIFY: "green",
  INJECT_SCORE: "grape",
};

export const AGGREGATION_LABELS: Record<ScoringAggregation, string> = {
  SUM: "Sum",
  AVERAGE: "Average",
  POINTS: "Points (W/L/D)",
  FORMULA: "Custom Formula",
};

export const TIEBREAKER_LABELS: Record<TiebreakerType, string> = {
  HEAD_TO_HEAD: "Head-to-head",
  SCORE_DIFFERENCE: "Score difference",
  TOTAL_SCORE: "Total score",
  WINS: "Wins",
  BUCHHOLZ: "Buchholz",
  SONNBORN_BERGER: "Sonnborn-Berger",
  MEDIAN_BUCHHOLZ: "Median Buchholz",
  CUMULATIVE: "Cumulative",
  CUSTOM_FORMULA: "Custom formula",
};

export const PRESET_LABELS: Record<PresetName, string> = {
  SINGLE_ELIM: "Single Elimination",
  DOUBLE_ELIM: "Double Elimination",
  DOUBLE_ELIM_TRUE_FINALS: "Double Elimination (True Finals)",
  TRIPLE_ELIM: "Triple Elimination",
  ROUND_ROBIN: "Round Robin",
  ROUND_ROBIN_BO3: "Round Robin Bo3",
  LEAGUE_HOME_AWAY: "Home & Away League",
  SWISS: "Swiss System",
  SWISS_ACCELERATED: "Accelerated Swiss",
  SWISS_MCMAHON: "McMahon Swiss",
  BUCHHOLZ_GROUPS: "Buchholz Groups",
  SINGLE_ELIM_CONSOLATION: "Single Elim + Consolation",
  RANDOM_DRAW_KNOCKOUT: "Random Draw Knockout",
  STEP_LADDER: "Step Ladder",
  PAGE_PLAYOFF: "Page Playoff",
  GAUNTLET: "Gauntlet",
  GSL_FORMAT: "GSL Format",
  ROUND_ROBIN_PLAYOFFS: "RR + Playoffs",
  BATTLE_ROYALE: "Battle Royale",
  POINTS_RACE: "Points Race",
  CONQUEST: "Conquest",
  KING_OF_THE_HILL_BO5: "King of the Hill Bo5",
};
